import { CommunityMembershipRole } from '../enums';
import { DomainError } from '../errors/domain-error';
import type { CommunityMembershipContext } from './community-post.policy';

export const CommunityModeratorPermission = {
  MANAGE_POSTS: 'manage_posts',
  PIN_POSTS: 'pin_posts',
  SET_POST_LEVEL_GATES: 'set_post_level_gates',
  MANAGE_COMMENTS: 'manage_comments',
  MANAGE_MEMBERS: 'manage_members',
  MANAGE_REPORTS: 'manage_reports',
  MANAGE_SPAM_SETTINGS: 'manage_spam_settings',
  VIEW_AUDIT_LOG: 'view_audit_log',
  EXPORT_MEMBERS: 'export_members',
  EXPORT_PAYMENTS: 'export_payments',
} as const;

export type CommunityModeratorPermissionValue =
  (typeof CommunityModeratorPermission)[keyof typeof CommunityModeratorPermission];

export const ALL_COMMUNITY_MODERATOR_PERMISSIONS: CommunityModeratorPermissionValue[] = [
  CommunityModeratorPermission.MANAGE_POSTS,
  CommunityModeratorPermission.PIN_POSTS,
  CommunityModeratorPermission.SET_POST_LEVEL_GATES,
  CommunityModeratorPermission.MANAGE_COMMENTS,
  CommunityModeratorPermission.MANAGE_MEMBERS,
  CommunityModeratorPermission.MANAGE_REPORTS,
  CommunityModeratorPermission.MANAGE_SPAM_SETTINGS,
  CommunityModeratorPermission.VIEW_AUDIT_LOG,
  CommunityModeratorPermission.EXPORT_MEMBERS,
  CommunityModeratorPermission.EXPORT_PAYMENTS,
];

export const COMMUNITY_MODERATOR_PERMISSION_LABELS: Record<
  CommunityModeratorPermissionValue,
  string
> = {
  [CommunityModeratorPermission.MANAGE_POSTS]: 'Manage posts',
  [CommunityModeratorPermission.PIN_POSTS]: 'Pin & announce posts',
  [CommunityModeratorPermission.SET_POST_LEVEL_GATES]: 'Set level gates',
  [CommunityModeratorPermission.MANAGE_COMMENTS]: 'Manage comments',
  [CommunityModeratorPermission.MANAGE_MEMBERS]: 'Ban & remove members',
  [CommunityModeratorPermission.MANAGE_REPORTS]: 'Moderate reports',
  [CommunityModeratorPermission.MANAGE_SPAM_SETTINGS]: 'Spam & slow mode settings',
  [CommunityModeratorPermission.VIEW_AUDIT_LOG]: 'View audit log',
  [CommunityModeratorPermission.EXPORT_MEMBERS]: 'Export members CSV',
  [CommunityModeratorPermission.EXPORT_PAYMENTS]: 'Export payments CSV',
};

export function parseStoredModeratorPermissions(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === 'string');
}

export function resolveCommunityModeratorPermissions(
  role: CommunityMembershipRole | string,
  stored: string[] | null | undefined,
  roleTemplatePermissions?: string[] | null,
): CommunityModeratorPermissionValue[] {
  if (role === CommunityMembershipRole.OWNER) {
    return [...ALL_COMMUNITY_MODERATOR_PERMISSIONS];
  }
  if (role !== CommunityMembershipRole.MODERATOR) {
    return [];
  }
  const template = roleTemplatePermissions?.length
    ? roleTemplatePermissions.filter((permission): permission is CommunityModeratorPermissionValue =>
        ALL_COMMUNITY_MODERATOR_PERMISSIONS.includes(permission as CommunityModeratorPermissionValue),
      )
    : null;
  if (template?.length) {
    return template;
  }
  if (!stored?.length) {
    return [...ALL_COMMUNITY_MODERATOR_PERMISSIONS];
  }
  const allowed = new Set<string>(ALL_COMMUNITY_MODERATOR_PERMISSIONS);
  return stored.filter((permission): permission is CommunityModeratorPermissionValue =>
    allowed.has(permission),
  );
}

export function hasCommunityModeratorPermission(
  membership: Pick<
    CommunityMembershipContext,
    'role' | 'moderatorPermissions' | 'moderatorRolePermissions'
  >,
  permission: CommunityModeratorPermissionValue | string,
): boolean {
  const resolved = resolveCommunityModeratorPermissions(
    membership.role,
    membership.moderatorPermissions ?? null,
    membership.moderatorRolePermissions ?? null,
  );
  return resolved.includes(permission as CommunityModeratorPermissionValue);
}

export function assertCommunityModeratorPermission(
  membership: Pick<
    CommunityMembershipContext,
    'role' | 'moderatorPermissions' | 'moderatorRolePermissions'
  >,
  permission: CommunityModeratorPermissionValue | string,
): void {
  if (!hasCommunityModeratorPermission(membership, permission)) {
    throw new DomainError(`Missing moderator permission: ${permission}`);
  }
}

export function normalizeDelegatedModeratorPermissions(
  permissions: string[] | undefined | null,
): CommunityModeratorPermissionValue[] {
  if (!permissions?.length) {
    return [...ALL_COMMUNITY_MODERATOR_PERMISSIONS];
  }
  const allowed = new Set<string>(ALL_COMMUNITY_MODERATOR_PERMISSIONS);
  const unique = [...new Set(permissions.filter((permission) => allowed.has(permission)))];
  if (unique.length === 0) {
    throw new DomainError('At least one moderator permission is required');
  }
  return unique as CommunityModeratorPermissionValue[];
}
